import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {gsap, TweenLite} from "gsap";
import {FreespinSymbol, FSSymbolTypes, IFsSymbolData} from "../../symbols/freespin/FreespinSymbol";
import {FireInTheHole2} from "../../FireInTheHole2";
import {FITH2GameConfig} from "../../configs/FITH2GameConfig";
import {FreespinReelView} from "./FreespinReelView";

export enum SimpleReelModelState {
    IDLE = "IDLE",
    START = "START",
    SPIN = "SPIN",
    STOP = "STOP",
    STOPPED = "STOPPED",
    BOUNCE = "BOUNCE",
}

export interface SimpleReelPaddingData {
    visualPaddingBefore: number;
    paddingBefore: number;
    visibleSymbols: number;
    paddingAfter: number;
    visualPaddingAfter: number;
}

export class FreespinReelModel {
    public readonly events: APIEventSystem | undefined;
    public readonly id: number;
    private _isRevealed: boolean;
    private _symbol: FreespinSymbol | null;
    private _isDirt: boolean;
    private _view: FreespinReelView;

    constructor(reelId: number, symId: number, triggerEvents: boolean = false) {
        this.id = reelId * 4 + symId;
        this._isRevealed = false;
        this._isDirt = false;
        this._symbol = null;
        this._view = new FreespinReelView(reelId, symId);
        this._view.position.set(reelId * FITH2GameConfig.fsSymbolWidth, symId * FITH2GameConfig.symbolHeight);
    }

    private removeSymbol(symbol: FreespinSymbol): void {
        if (symbol.parent) {
            symbol.parent.removeChild(symbol);
        }
        symbol.remove();
    }

    public show(): void {
        this._view.show(this._isRevealed);
    }

    protected createNewSymbol(symData: IFsSymbolData, position: number, state: SimpleReelModelState, isRestore: boolean = false, hidden: boolean = false): FreespinSymbol {
        const symbol = new FreespinSymbol(symData, state, isRestore);
        symbol.reelPosition = position;

        if (symData.type) {
            if (symData.symName === "D") {
                symbol.isDirt = true;
                this._isDirt = true;
                this._symbol = symbol;
            }
        } else {
            symbol.name = "spin" + position;
        }
        this._view.spinContainer.addChild(symbol);
        return symbol;
    }

    protected clearSymbols(symbols: FreespinSymbol[]): void {
        let symbol;
        while (symbol = symbols.pop()) {
            this.removeSymbol(symbol);
        }
    }

    private _triggerTime: number;
    public illegalVisualPaddingSymbols: string[] = [];
    public active: boolean = true;

    get reelSet(): string[] {
        if (!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.id}`);
        }
        return this._reelSet;
    }

    get state(): SimpleReelModelState {
        return this._state;
    }

    set state(value: SimpleReelModelState) {
        this._state = value;
    }

    get symbols(): FreespinSymbol[] {
        return this._symbols;
    }


    //Fake reel
    private _reelSet: string[] | undefined;
    private _reelSetIndex: number = 0;

    private _stopIndex: number;
    private _symbols: FreespinSymbol[] = [];

    public paddingConfig: SimpleReelPaddingData = {
        visualPaddingBefore: 1,
        paddingBefore: 0,
        visibleSymbols: 1,
        paddingAfter: 0,
        visualPaddingAfter: 1,
    }


    public animatedStepData = {
        x: 0,
        y: 0
    }

    public stepSize: number = 100;
    private _spinning: boolean = false;
    private _currentSpinStep: gsap.core.Timeline | undefined;

    private animationTimeScale = 1;
    public stepDuration = 0.4;
    private _stopTimeline: gsap.core.Timeline | undefined;
    private _stopping: boolean = false;
    private triggerStop: boolean = false;
    private masterSpinTimeline: gsap.core.Timeline | undefined;
    private _state: SimpleReelModelState = SimpleReelModelState.IDLE;

    public setReelSet(reelSet: string[], index?: number) {
        this._reelSet = reelSet;
        index = index != undefined ? index : Math.floor(Math.random() * this._reelSet.length);
        this.setReelIndex(index);
    }

    private getNextsymName(nextSymbolPosition: number): string {
        let nextIndex = this._reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this.reelSet.length + nextIndex;
        }
        if (nextIndex >= this.reelSet.length) {
            nextIndex = nextIndex - this.reelSet.length;
        }
        return this.reelSet[nextIndex];
    }

    private getNextSymbol(position: number, state: SimpleReelModelState): FreespinSymbol {
        //Stoppin

        // Ugly fix to make a temporary padding symbol behind xNudges instead of just hiding the actual symbol.
        // Just hiding the symbol may cause a crash when the symbol behind is a xWays symbol
        return this.createNewSymbol({symName: this.getNextsymName(position)}, position, SimpleReelModelState.SPIN);
    }

    private getRandomSymbolVisualPaddingSymbol(): string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        let symbol = this.reelSet[randomIndex];
        if (this.illegalVisualPaddingSymbols.indexOf(symbol) >= 0) {
            symbol = this.getRandomSymbolVisualPaddingSymbol();
        }
        return symbol;
    }

    //Public functions

    private resetSymbols(symData: IFsSymbolData[]): void {
        this.clearSymbols(this._symbols);

        const totalInitCount = this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter;

        if (symData.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.id}`);
        }
        const sym0: string = this.getRandomSymbolVisualPaddingSymbol(),
            sym1: string = this.getRandomSymbolVisualPaddingSymbol();
        symData.unshift({symName: sym0, state: SimpleReelModelState.STOPPED});
        symData.push({symName: sym1, state: SimpleReelModelState.STOPPED});
        for (let i: number = symData.length - 1; i >= 0; i--) {
            const symbol = this.createNewSymbol(symData[i], i, SimpleReelModelState.SPIN);
            this._symbols.push(symbol);

            if (symData[i].type === FSSymbolTypes.DIRT || i === 1) {
                this._symbol = this.createNewSymbol(symData[i], i, SimpleReelModelState.IDLE);
                symbol.visible = false;
            }

        }
        this.updatePosition();
    }

    public init(symData: IFsSymbolData[]) {
        this._view.init();
        this.resetSymbols(symData);
    }

    public reset(): void {
        this._isRevealed = false;
        this._isDirt = false;
        this.removeSymbol(this._symbol!);
        this._symbol = null;
        this._view.hide();
        this.resetSymbols([FireInTheHole2.model.defaultFSReelData[Math.floor(this.id / 4)][this.id % 4 + 1]]);
    }

    public restore(symData: IFsSymbolData[]): void {
        this._isRevealed = !(symData[0].symName === "M" || symData[0].symName === "D");

        if (!this._isRevealed) {
            return;
        }
        this.clearSymbols(this._symbols);
        this._spinning = false;
        this._stopping = false;
        this.state = SimpleReelModelState.IDLE;
        const symbol = this.createNewSymbol(symData[0], 1, SimpleReelModelState.STOP, true);
        this._symbol = symbol;
    }

    public prepareStop(symData: IFsSymbolData, anticipation: boolean = false): void {
        const symbol = this.createNewSymbol(symData, 1, SimpleReelModelState.STOP, false);
        symbol.position.y = -100;
        symbol.name = "stop_symbol" + symbol.reelPosition;
        this._symbol = symbol;
    }

    public stopped(): TweenLite {
        return TweenLite.to(this._symbol!, {
            y: 0,
            duration: 0
        });
    }
    public stop(symData: IFsSymbolData, anticipation: boolean = false): gsap.core.Timeline {
        if (this.isPC) {
            return gsap.timeline();
        }

        if (this.state != SimpleReelModelState.START && this.state != SimpleReelModelState.SPIN) {
            return gsap.timeline();
        }
        if (this._stopTimeline) {
            if (this._stopTimeline.isActive()) {
                this._stopTimeline.pause();
                this._stopTimeline.kill();
            }
            this._stopTimeline = undefined;
        }
        this.prepareStop(symData, false);
        this._stopTimeline = gsap.timeline({paused: true});
        this._stopTimeline!.add(() => {
            this.state = SimpleReelModelState.STOP;
            this._spinning = false;
            this._stopping = true;
            this._triggerTime = Date.now();
        });
        for (let i: number = 0; i < 3; i++) {
            this._stopTimeline!.add(this.step());
        }
        this._stopTimeline!.add(gsap.to(this._symbol!, {
            duration: this.stepDuration * 1.5,
            y: -100,
        }), 1 * this.stepDuration);
        this._stopTimeline!.add([
            gsap.to(this._symbol!, {
                duration: this.stepDuration * 1.5,
                y: this.stepSize * 0.1,
                ease: "power2.out"
            })
        ], 1 * this.stepDuration);
        this._stopTimeline!.add([
            gsap.to(this._symbol!, {
                y: 0,
                duration: this.stepDuration,
                ease: "power2.out"
            }),
            this._symbol!.stop(symData)
        ], 2.5 * this.stepDuration);
        const isMiss: boolean = symData.symName === "M";
        this._stopTimeline!.add([
            this._view.stop(),
            () => {
                this.state = SimpleReelModelState.STOPPED;
                this._stopTimeline = undefined;
                this._stopping = false;
                if (this._symbol!.isRemoved) {
                    debugger
                }
            }]);
        this._stopTimeline!.add(this._view.clearSpinContainer(isMiss));
        this._stopTimeline!.add(() => {
            if (isMiss) {
                this._view.spinContainer.addChild(this._symbol!);
                this._view.removeChild(this._symbol!);
            } else {
                this._view.addChild(this._symbol!);
            }
            this._view.removeUpgradeFrameAfterHit();
        });
        return this._stopTimeline!.play(0);
    }

    public revealStopSymbol(symData: IFsSymbolData): gsap.core.Timeline {
        const tl = gsap.timeline();
        this._isRevealed = true;
        tl.add([
            ()=>{this._view.addChild(this._symbol!);},
            this._symbol!.dropValueFromTopTrain()
        ], 0);
        return tl;
    }

    public prepareStart(): gsap.core.Timeline {
        const tl = gsap.timeline();
        if (this.isPC) {
            tl.add(this._symbol!.shakePCChainAnimation(), 0);
        }
        tl.add(
            () => {
                this._view.removeUpgradeFrameSpinStart();
                this._symbols.forEach((sym: FreespinSymbol) => {
                    sym.isSpinning = true;
                });
            }, 0);
        if (this._isDirt || this._isRevealed) {
            return tl;
        }

        tl.add(() => {
            this.state = SimpleReelModelState.START;
        }, 0);
        return tl;
    }

    public start(): gsap.core.Timeline {
        const tl = gsap.timeline();

        if (this.isPC) {
            tl.add([this._view.startPCAnimation(),
                this.getVisibleSymbol().removeChain()]);
        }
        if (this._isDirt || this._isRevealed) {
            return tl;
        }

        tl.add(this.step(true));
        tl.add(() => {
            if (this._symbol && this._symbol.parent) {
                this._symbol.parent.removeChild(this._symbol);
                this._view.spinContainer.addChild(this._symbol);
            }
            this.startSpin();
        });

        return tl;
    }

    private startSpin(reverse: boolean = false) {
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this.masterSpinTimeline = gsap.timeline();
        this.masterSpinTimeline!.add(this.spin());
    }

    private spin(): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(this.step());
        tl.add(() => {
            if (this._spinning) {
                this.spin();
            }
        });
        return tl;
    }

    private step(start: boolean = false): gsap.core.Timeline {
        const tween = gsap.timeline();
        tween.add(() => this.onAnimationStart(tween), 0);
        if (start) {
            const dropSymbol:FreespinSymbol = this._symbol!;
            tween.add([
                ()=>{
                    const symbolsLen:number = this._symbols.length;
                    this._symbols.forEach((sym:FreespinSymbol, i:number)=>{
                        sym.reelPosition = symbolsLen-1-i;
                    });
                    this.animatedStepData.x = 0;
                    this.animatedStepData.y = 0;
                    this.updatePosition();
                },
                gsap.to(dropSymbol, {
                    duration: 0.2,
                    y: -20,
                    ease: "power2.out",
                })
            ],0);
            tween.add(()=>{
                TweenLite.to(dropSymbol, {
                    duration: this.stepDuration * 1.5,
                    y: 100,
                    ease: "power2.out",
                })});
            return tween;
        }

        tween.add(gsap.to(this.animatedStepData, {
            duration: this.stepDuration,
            y: this.stepSize,
            ease: "none",
            onUpdate: () => this.updatePosition()
        }));
        tween.add(() => this.onAnimationComplete());
        return tween;
    }

    private onAnimationStart(tween: gsap.core.Timeline): void {
        this._currentSpinStep = tween;
        //if(this._view.hasRemovedAllChildren){
        // return
        // }
        this.addSymbol();
    }

    private onAnimationComplete() {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            symbol.reelPosition++;
        }

        this.animatedStepData.x = 0;
        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline!.kill();
            this._stopTimeline!.play(0);
        }
        this.updateIndex();
        this.updatePosition();
        this.removeSymbols();
    }

    private updateIndex() {
        const valueChange = -1
        if (this._stopping) {
            this.setStopIndex(this._stopIndex + valueChange);
        } else {
            this.setReelIndex(this._reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value: number) {
        this._stopIndex = value;
    }

    private setReelIndex(value: number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this._reelSetIndex = value;
    }

    private addSymbol(): void {
        let newSymbolPosition = -1;
        if (this._stopping) {
            return;
        }
        const nextSymbol = this.getNextSymbol(newSymbolPosition, this.state);
        nextSymbol.position.y = -100;
        this._symbols.unshift(nextSymbol);
    }

    private removeSymbols(): void {
        for (let i: number = this._symbols.length - 1; i >= 0; i--) {
            const symbol = this._symbols[i];
            if (symbol.reelPosition > 1 && symbol.isSpinning) {
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this.removeSymbol(removedSymbol);
            }
        }
    }

    private updatePosition() {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol: FreespinSymbol = this._symbols[i];
            const basePosY = this.stepSize * symbol.reelPosition - 100;
            if (symbol.children.length > 0) {
                symbol.position.y = basePosY + this.animatedStepData.y;
            }
        }
    }

    public get isRevealed(): boolean {
        return this._isRevealed;
    }

    public set stopping(value: boolean) {
        this.triggerStop = value;
    }

    public get isDirt(): boolean {
        return this._isDirt;
    }

    public get isPC(): boolean {
        return this._symbol! && this._symbol!.isPC;
    }

    public get view(): FreespinReelView {
        return this._view;
    }

    public getVisibleSymbol(): FreespinSymbol {
        if (!this._symbol) {
            debugger
        }
        return this._symbol!;
    }

    public getDynamite(stepId: number = -1, explodeOnTopLayer: boolean = false): gsap.core.Timeline {
        const tl = gsap.timeline(), dirtExplode: boolean = this._isDirt;
        if (dirtExplode) {
            this._view.parent.addChild(this._view);
            tl.add([
                () => {
                    this._view.addChild(this._symbol!);
                },
                this._view.clearSpinContainer(false)
            ]);
        }
        tl.add(this._symbol!.getDynamite(stepId, explodeOnTopLayer));
        this._isDirt = false;
        if (dirtExplode) {
            tl.add(this._view.clearSpinContainer(true));
            tl.add(() => {
                this._view.spinContainer.addChild(this._symbol!);
                this._view.removeChild(this._symbol!);
            });
        }
        return tl;
    }

    public getReelMultiplier(stepId: number = -1): gsap.core.Timeline {
        return gsap.timeline().add([this._view.getReelMultiplier(), this._symbol!.getReelMultiplier(stepId)]);
    }
}